import React, { forwardRef, ReactNode } from 'react'
import clsx from 'clsx'
import { Tab as TabStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps, PolymorphicPropsWithRef, PolymorphicRef } from '../helperTypes'

const useStyles = createUseStyles(TabStyles, 'tab')

type Props = {
  /** Disabled state */
  disabled?: boolean
  /** URL of the page the tab goes to */
  href?: string
  /** Icon component */
  icon?: ReactNode
  onChange?: (event: React.MouseEvent<{}>, value: any) => void
  /** selected state */
  selected?: boolean
  /**
   * the size of the tab
   * @default medium
   */
  size?: 'medium' | 'large' | 'xlarge'
  /** Specifies where to open the linked document */
  target?: '_blank' | '_parent' | '_self' | '_top' | string
  /** You can provide your own value for each tab */
  value?: any
  /**
   * Change the look of the Tab and Tabs using variant
   * @default default
   */
  variant?: 'default' | 'container'
}

type DeprecatedProps = {
  label?: string | ReactNode
}

export type TabProps<C extends React.ElementType> = PolymorphicPropsWithRef<C, Props & DeprecatedProps & CommonProps>

// type annotation for strong typing of polymorphic ref
type PolymorphicTab = <C extends React.ElementType = 'a'>(props: TabProps<C>) => React.ReactElement | null

export const Tab: PolymorphicTab = forwardRef(
  <C extends React.ElementType>(props: TabProps<C>, ref?: PolymorphicRef<C>) => {
    const { children, className, label, component, size, disabled, icon, selected, value, variant, onChange, ...rest } =
      props
    const ComponentProp = component || 'a'
    const classes = useStyles()
    useDeprecatedProps('Tab', { label: 'children' }, props)
    return (
      <li role='presentation'>
        <ComponentProp
          ref={ref}
          role='tab'
          aria-selected={selected}
          className={clsx(
            className,
            classes.root,
            size && classes[`_${size}`],
            disabled && classes._disabled,
            selected && classes._selected,
            variant === 'container' && classes[`_${variant}`],
          )}
          tabIndex={selected ? 0 : -1}
          onClick={(event: any) => {
            if (!disabled && onChange) onChange(event, value)
          }}
          {...rest}
        >
          {icon}
          {label || children}
        </ComponentProp>
      </li>
    )
  },
)

export default Tab
